import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  CardActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Paper,
  Divider,
  Stack,
  CircularProgress,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Schedule as ScheduleIcon,
  Visibility as PreviewIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  CalendarMonth as CalendarIcon,
  Settings as SettingsIcon,
  LocationOn as LocationIcon,
  LocalOffer as KeywordsIcon,
  Publish as PublishIcon,
  PostAdd as PostIcon,
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Image as ImageIcon,
  AttachMoney as CostIcon,
  PhotoCamera as PhotoIcon,
  Place as PlaceIcon
} from '@mui/icons-material';
import { format, addDays, isWeekend } from 'date-fns';
import { de } from 'date-fns/locale';

// Separate StrategyStep component to prevent focus loss
const StrategyStep = memo(({
  strategies,
  wizardData,
  handleStrategyChange,
  handleCustomPostSettingsChange,
  availableKeywords,
  customKeyword,
  handleCustomKeywordChange,
  handleKeyPressCustomKeyword,
  handleAddCustomKeyword,
  removeKeyword,
  getKeywordRankingInfo,
  textFieldStyle,
  availableRegionalKeywords,
  customRegionalKeyword,
  handleCustomRegionalKeywordChange,
  handleKeyPressCustomRegionalKeyword
}) => (
  <Box sx={{ py: 2 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
      Wählen Sie Ihre Content-Strategie
    </Typography>
    
    {/* 🎯 INTELLIGENTE EMPFEHLUNGEN */}
    
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {Object.entries(strategies).map(([key, strategy]) => (
        <Grid item xs={12} md={4} key={key}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: wizardData.strategy === key ? 3 : 1,
              borderColor: wizardData.strategy === key ? strategy.color : 'divider',
              transition: 'all 0.3s ease',
              '&:hover': { 
                boxShadow: 6,
                borderColor: strategy.color
              }
            }}
            onClick={() => handleStrategyChange(key)}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Avatar 
                sx={{ 
                  bgcolor: strategy.color, 
                  width: 60, 
                  height: 60, 
                  mx: 'auto', 
                  mb: 2,
                  fontSize: '1.5rem',
                  fontWeight: 700
                }}
              >
                {strategy.postCount}
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {strategy.name}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {strategy.description}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Chip label={`${strategy.postCount} Posts`} size="small" />
                <Chip label={`Alle ${strategy.interval} Tage`} size="small" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    <Divider sx={{ my: 3 }} />

    {/* Custom Post Settings */}
    <Paper elevation={0} sx={{ p: 3, mb: 4, bgcolor: 'rgba(25, 118, 210, 0.05)', border: '1px solid', borderColor: 'primary.light', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon color="primary" />
        Benutzerdefinierte Einstellungen
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Anzahl der Posts"
            value={wizardData.postCount}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                handleCustomPostSettingsChange('postCount', '');
              } else {
                const numValue = parseInt(value);
                if (!isNaN(numValue) && numValue > 0) {
                  handleCustomPostSettingsChange('postCount', numValue);
                }
              }
            }}
            onFocus={(e) => {
              e.target.value = '';
              handleCustomPostSettingsChange('postCount', '');
            }}
            fullWidth
            placeholder="z.B. 2, 4, 5, 10, 15, 20"
            helperText="Geben Sie eine beliebige Zahl ein (z.B. 2, 4, 7, 12, 20)"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Intervall (Tage)"
            value={wizardData.intervalDays}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                handleCustomPostSettingsChange('intervalDays', '');
              } else {
                const numValue = parseInt(value);
                if (!isNaN(numValue) && numValue > 0) {
                  handleCustomPostSettingsChange('intervalDays', numValue);
                }
              }
            }}
            onFocus={(e) => {
              e.target.value = '';
              handleCustomPostSettingsChange('intervalDays', '');
            }}
            fullWidth
            placeholder="z.B. 1, 2, 3, 5, 7, 12, 14"
            helperText="Geben Sie eine beliebige Zahl ein (z.B. 1, 2, 5, 7, 12)"
          />
        </Grid>
      </Grid>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        💡 Die Strategie "{wizardData.strategy ? strategies[wizardData.strategy]?.name : 'Keine ausgewählt'}" wird mit {wizardData.postCount} Posts alle {wizardData.intervalDays} Tage angewendet.
      </Typography>
    </Paper>

    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 2,
            fontWeight: 600,
            color: 'text.primary' 
          }}>
            <KeywordsIcon color="primary" />
            Haupt-Keywords
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Wählen Sie Keywords aus dem Ranking-Tracker (mit Position angezeigt)
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {wizardData.targetKeywords.filter(k => k && k.trim()).map((keyword, index) => {
              const rankingInfo = getKeywordRankingInfo(keyword);
              return (
                <Chip
                  key={index}
                  label={rankingInfo ? `${keyword} (#${rankingInfo.rank})` : keyword}
                  onDelete={() => removeKeyword(keyword, 'primary')}
                  color={rankingInfo ? rankingInfo.color : "primary"}
                  variant="outlined"
                  sx={{
                    backgroundColor: rankingInfo ? rankingInfo.bgColor : 'transparent',
                    height: '36px',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    '& .MuiChip-label': {
                      color: 'text.primary'
                    }
                  }}
                />
              );
            })}
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Verfügbare Keywords:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', maxHeight: 200, overflowY: 'auto' }}>
            {availableKeywords.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion.keyword}
                onClick={() => handleAddCustomKeyword(suggestion.priority === 'regional' ? 'regional' : 'primary', suggestion.keyword)}
                variant="outlined"
                size="small"
                color={suggestion.priority === 'primary' ? 'primary' : suggestion.priority === 'regional' ? 'secondary' : 'default'}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { 
                    backgroundColor: 'action.hover' 
                  }
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Benutzerdefiniertes Schlüsselwort"
              value={customKeyword}
              onChange={handleCustomKeywordChange}
              placeholder="z.B. Terrassendach Montage"
              onKeyPress={handleKeyPressCustomKeyword}
              style={textFieldStyle}
            />
            <Button 
              variant="contained" 
              size="small"
              onClick={() => handleAddCustomKeyword('primary')}
              disabled={!customKeyword.trim()}
            >
              Hinzufügen
            </Button>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 2,
            fontWeight: 600,
            color: 'text.primary' 
          }}>
            <PlaceIcon color="primary" />
            Regional-Keywords
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Keywords mit lokalem Bezug für bessere lokale Sichtbarkeit
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {wizardData.targetRegionalKeywords.filter(k => k && k.trim()).map((keyword, index) => {
              const rankingInfo = getKeywordRankingInfo(keyword);
              return (
                <Chip
                  key={index}
                  label={rankingInfo ? `${keyword} (#${rankingInfo.rank})` : keyword}
                  onDelete={() => removeKeyword(keyword, 'regional')}
                  color={rankingInfo ? rankingInfo.color : "secondary"}
                  variant="outlined"
                  sx={{
                    backgroundColor: rankingInfo ? rankingInfo.bgColor : 'transparent',
                    height: '36px',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    '& .MuiChip-label': {
                      color: 'text.primary'
                    }
                  }}
                />
              );
            })}
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Verfügbare Regional-Keywords:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', maxHeight: 200, overflowY: 'auto' }}>
            {availableRegionalKeywords.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion.keyword}
                onClick={() => handleAddCustomKeyword('regional', suggestion.keyword)}
                variant="outlined"
                size="small"
                color={suggestion.priority === 'primary' ? 'secondary' : 'default'}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { 
                    backgroundColor: 'action.hover' 
                  }
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Benutzerdefiniertes regionales Schlüsselwort"
              value={customRegionalKeyword}
              onChange={handleCustomRegionalKeywordChange}
              placeholder="z.B. Montage Stuttgart"
              onKeyPress={handleKeyPressCustomRegionalKeyword}
              style={textFieldStyle}
            />
            <Button 
              variant="contained" 
              size="small"
              onClick={() => handleAddCustomKeyword('regional')}
              disabled={!customRegionalKeyword.trim()}
            >
              Hinzufügen
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  </Box>
));

const PostsWizard = ({ open, onClose, selectedBusiness, keywords, regionalKeywords, rankingData, existingPosts = {}, onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, step: '' });
  const [editingPost, setEditingPost] = useState(null);
  const [editingMediaUrl, setEditingMediaUrl] = useState('');
  const [editingMediaFile, setEditingMediaFile] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Custom keyword input states
  const [customKeyword, setCustomKeyword] = useState('');
  const [customRegionalKeyword, setCustomRegionalKeyword] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);

  // 📊 INTELLIGENTE POST-ANALYSE
  const analyzeExistingPosts = () => {
    const allPosts = [
      ...(existingPosts.suggestions || []),
      ...(existingPosts.scheduled || []),
      ...(existingPosts.published || []),
      ...(existingPosts.drafts || [])
    ];

    const analysis = {
      totalPosts: allPosts.length,
      publishedPosts: (existingPosts.published || []).length,
      scheduledPosts: (existingPosts.scheduled || []).length,
      suggestionPosts: (existingPosts.suggestions || []).length,
      draftPosts: (existingPosts.drafts || []).length,
      recentActivity: allPosts.filter(post => {
        const postDate = new Date(post.createTime || post.createdAt || Date.now());
        const thirtyDaysAgo = new Date(Date.now() - (30 * 24 * 60 * 60 * 1000));
        return postDate > thirtyDaysAgo;
      }).length,
      hasContent: allPosts.length > 0
    };

    return analysis;
  };

  const postAnalysis = analyzeExistingPosts();

  // 📅 Calculate default start date based on last scheduled post + interval days
  const calculateDefaultStartDate = () => {
    const scheduledPosts = existingPosts.scheduled || [];
    if (scheduledPosts.length === 0) {
      return new Date();
    }
    // Find the last scheduled post
    const lastScheduled = scheduledPosts.reduce((latest, post) => {
      const postDate = new Date(post.scheduledFor || post.createdAt);
      const latestDate = new Date(latest.scheduledFor || latest.createdAt);
      return postDate > latestDate ? post : latest;
    });

    const lastDate = new Date(lastScheduled.scheduledFor || lastScheduled.createdAt);
    // Add interval days to the last scheduled date
    const intervalDays = smartRecommendations?.intervalDays || 2;
    const defaultStart = addDays(lastDate, intervalDays);
    return defaultStart;
  };

  // 🎯 INTELLIGENTE EMPFEHLUNGEN basierend auf bestehenden Posts
  const getSmartRecommendations = () => {
    if (postAnalysis.totalPosts === 0) {
      return {
        strategy: 'balanced',
        postCount: 15,
        intervalDays: 2,
        recommendation: 'Perfekter Start! Wir empfehlen eine ausgewogene Strategie mit 15 Posts.'
      };
    } else if (postAnalysis.publishedPosts >= 10) {
      return {
        strategy: 'conservative',
        postCount: 8,
        intervalDays: 3,
        recommendation: `Großartig! Sie haben bereits ${postAnalysis.publishedPosts} Posts. Erweitern Sie Ihre Strategie konservativ.`
      };
    } else if (postAnalysis.recentActivity > 5) {
      return {
        strategy: 'aggressive',
        postCount: 20,
        intervalDays: 1,
        recommendation: `Starke Aktivität! Sie sind auf einem guten Weg. Verstärken Sie mit einer aggressiven Strategie.`
      };
    } else {
      return {
        strategy: 'balanced',
        postCount: 12,
        intervalDays: 2,
        recommendation: `Sie haben ${postAnalysis.totalPosts} Posts. Eine ausgewogene Strategie bringt Sie voran.`
      };
    }
  };
  
  const smartRecommendations = getSmartRecommendations();

  const [wizardData, setWizardData] = useState({
    // Step 1: Strategy
    strategy: smartRecommendations.strategy,
    targetKeywords: [],
    targetRegionalKeywords: [],
    postCount: smartRecommendations.postCount,
    
    // Step 2: Generation
    generatedPosts: [],
    selectedPosts: [],
    editedPosts: {}, // Stores edited versions of posts
    
    // Image Generation Options
    generateImages: false,
    imageQuality: 'medium', // 'low', 'medium', 'high'
    imageSize: '1024x1024', // GMB optimal size
    imageGeneration: {
      enabled: false,
      status: 'pending', // 'pending', 'generating', 'completed', 'error'
      results: {}, // postId -> { imageUrl, cost, status }
      totalCost: 0,
      estimatedCost: 0
    },
    
    // Step 3: Scheduling
    schedulingMode: 'interval', // 'interval', 'custom', 'workdays'
    intervalDays: smartRecommendations.intervalDays,
    startDate: calculateDefaultStartDate(),
    startTime: '09:00', // Standardzeit 9:00 Uhr
    excludeWeekends: false,

    // Step 5: Custom posts
    customPostContent: '',
    customPostDate: format(new Date(), 'yyyy-MM-dd'),
    customPosts: [],
    
    // Step 4: Preview
    scheduledPosts: [],
    publishingPlan: null
  });

  const steps = [
    {
      label: 'Content-Strategie',
      description: 'Keywords und Ziele definieren',
      icon: <SettingsIcon />
    },
    {
      label: 'Posts generieren',
      description: 'KI-optimierte Inhalte erstellen',
      icon: <AIIcon />
    },
    {
      label: 'Posts auswählen',
      description: 'Beste Inhalte für Ihre Kampagne',
      icon: <CheckIcon />
    },
    {
      label: 'Zeitplan erstellen',
      description: 'Veröffentlichungsintervall festlegen',
      icon: <ScheduleIcon />
    },
    {
      label: 'Vorschau & Start',
      description: 'Finale Überprüfung und Aktivierung',
      icon: <PreviewIcon />
    }
  ];

  // Derive available keywords from props
  const availableMainKeywords = (keywords || []).filter(k => 
    k && !wizardData.targetKeywords.includes(k)
  );
  
  const availableRegionalKeywords = (regionalKeywords || []).filter(k => 
    k && !wizardData.targetRegionalKeywords.includes(k)
  );

  // Combine all available keywords for display
  const availableKeywords = [
    ...availableMainKeywords.map(k => ({ keyword: k, priority: 'primary' })),
    ...availableRegionalKeywords.map(k => ({ keyword: k, priority: 'regional' }))
  ];

  const strategies = {
    aggressive: {
      name: 'Aggressive Local SEO',
      description: 'Maximale Sichtbarkeit in kürzester Zeit',
      postCount: 25,
      interval: 1,
      keywordFocus: 'comprehensive',
      color: '#f44336'
    },
    balanced: {
      name: 'Ausgewogene Strategie',
      description: 'Optimales Verhältnis von Qualität und Quantität',
      postCount: 15,
      interval: 2,
      keywordFocus: 'balanced',
      color: '#ff9800'
    },
    conservative: {
      name: 'Nachhaltige Präsenz',
      description: 'Langfristige, qualitätsfokussierte Sichtbarkeit',
      postCount: 10,
      interval: 3,
      keywordFocus: 'focused',
      color: '#4caf50'
    }
  };

  useEffect(() => {
    if (open && !hasInitialized) {
      resetWizard();
      setHasInitialized(true);
    } else if (!open) {
      setHasInitialized(false);
    }
  }, [open, hasInitialized]);

  const resetWizard = () => {
    setActiveStep(0);
    setGenerating(false);
    setWizardData({
      strategy: 'balanced',
      targetKeywords: [...keywords.slice(0, rankingData.trackedKeywords.length)],
      targetRegionalKeywords: [...regionalKeywords.slice(0, rankingData.trackedKeywords.length)],
      postCount: 15,
      generatedPosts: [],
      selectedPosts: [],
      editedPosts: {},
      
      // Image Generation Options
      generateImages: false,
      imageQuality: 'medium',
      imageSize: '1024x1024',
      imageGeneration: {
        enabled: false,
        status: 'pending',
        results: {},
        totalCost: 0,
        estimatedCost: 0
      },
      
      schedulingMode: 'interval',
      intervalDays: 2,
      startDate: calculateDefaultStartDate(),
      startTime: '09:00',
      excludeWeekends: false,
      customPostContent: '',
      customPostDate: format(new Date(), 'yyyy-MM-dd'),
      customPosts: [],
      scheduledPosts: [],
      publishingPlan: null
    });
    
    // Reset custom keyword inputs
    setCustomKeyword('');
    setCustomRegionalKeyword('');
  };

  const handleNext = () => {
    const nextStep = activeStep + 1;
    
    // Automatisch Schedule berechnen beim Übergang zu Step 4 (Preview)
    if (nextStep === 4 && !wizardData.publishingPlan) {
      calculateScheduleAndNext();
      return;
    }
    
    setActiveStep(nextStep);
  };

  const calculateScheduleAndNext = () => {
    const selectedPostsData = wizardData.generatedPosts.filter(post =>
      wizardData.selectedPosts.includes(post.id)
    );

    let scheduledPosts = [];
    let currentDate = new Date(wizardData.startDate);

    selectedPostsData.forEach((post, index) => {
      // Skip weekends if option is enabled
      while (wizardData.excludeWeekends && isWeekend(currentDate)) {
        currentDate = addDays(currentDate, 1);
      }

      // Combine date with selected time in German timezone (CET/CEST)
      const [hours, minutes] = wizardData.startTime.split(':');
      const scheduledDateTime = new Date(currentDate);
      scheduledDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

      // Ensure the date is treated as German local time
      // This prevents UTC conversion issues when scheduling posts
      const germanOffset = scheduledDateTime.getTimezoneOffset() * 60000;
      const germanTime = new Date(scheduledDateTime.getTime() - germanOffset);

      // For debugging: log the scheduled time
      console.log(`📅 Scheduling post for: ${germanTime.toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}`);

      scheduledPosts.push({
        ...post,
        content: getPostContent(post), // Use edited content if available
        mediaUrl: post.mediaUrl || '', // Include media URL if available
        scheduledFor: germanTime,
        dayNumber: index + 1,
        status: 'scheduled'
      });

      // Move to next date based on interval
      if (wizardData.schedulingMode === 'interval') {
        currentDate = addDays(currentDate, wizardData.intervalDays);
      } else if (wizardData.schedulingMode === 'workdays') {
        do {
          currentDate = addDays(currentDate, 1);
        } while (isWeekend(currentDate));
      }
    });

    // Add custom posts to scheduled posts
    const allScheduledPosts = [...scheduledPosts, ...wizardData.customPosts];

    setWizardData(prev => ({
      ...prev,
      scheduledPosts: allScheduledPosts,
      publishingPlan: {
        totalPosts: allScheduledPosts.length,
        duration: allScheduledPosts.length > 0
          ? Math.ceil((allScheduledPosts[allScheduledPosts.length - 1]?.scheduledFor - wizardData.startDate) / (1000 * 60 * 60 * 24))
          : 0,
        startDate: wizardData.startDate,
        endDate: allScheduledPosts[allScheduledPosts.length - 1]?.scheduledFor,
        frequency: `Alle ${wizardData.intervalDays} Tage`,
        keywordCoverage: calculateKeywordCoverage(selectedPostsData)
      }
    }));

    // Direkt zum nächsten Step ohne weiteren handleNext Aufruf
    setActiveStep(4);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleStrategyChange = (strategy, customSettings = null) => {
    // If customSettings is provided, it's from the custom input fields
    if (customSettings) {
      setWizardData(prev => ({
        ...prev,
        ...customSettings
      }));
    } else if (strategy) {
      // Normal strategy selection
      const strategyConfig = strategies[strategy];
      setWizardData(prev => ({
        ...prev,
        strategy,
        postCount: strategyConfig.postCount,
        intervalDays: strategyConfig.interval
      }));
    }
  };

  const handleCustomPostSettingsChange = (field, value) => {
    setWizardData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addKeyword = (keyword, type) => {
    if (type === 'primary') {
      setWizardData(prev => ({
        ...prev,
        targetKeywords: prev.targetKeywords.includes(keyword) 
          ? prev.targetKeywords 
          : [...prev.targetKeywords, keyword]
      }));
    } else {
      setWizardData(prev => ({
        ...prev,
        targetRegionalKeywords: prev.targetRegionalKeywords.includes(keyword)
          ? prev.targetRegionalKeywords
          : [...prev.targetRegionalKeywords, keyword]
      }));
    }
  };

  const removeKeyword = (keyword, type) => {
    if (type === 'primary') {
      setWizardData(prev => ({
        ...prev,
        targetKeywords: prev.targetKeywords.filter(k => k !== keyword)
      }));
    } else {
      setWizardData(prev => ({
        ...prev,
        targetRegionalKeywords: prev.targetRegionalKeywords.filter(k => k !== keyword)
      }));
    }
  };

  // Handle custom keyword input
  const handleAddCustomKeyword = useCallback((type, customKeywordParam = null) => {
    // Use passed keyword or current input based on type
    const keyword = customKeywordParam || (type === 'primary' ? customKeyword : customRegionalKeyword);
    
    if (!keyword || !keyword.trim()) return;
    
    // Check for duplicates
    const existingKeywords = type === 'primary' ? wizardData.targetKeywords : wizardData.targetRegionalKeywords;
    if (existingKeywords.includes(keyword.trim())) {
      return; // Don't add duplicate
    }
    
    // Add the keyword
    addKeyword(keyword.trim(), type);
    
    // Clear the input only if no custom keyword was passed
    if (!customKeywordParam) {
      if (type === 'primary') {
        setCustomKeyword('');
      } else {
        setCustomRegionalKeyword('');
      }
    }
  }, [customKeyword, customRegionalKeyword, wizardData.targetKeywords, wizardData.targetRegionalKeywords, addKeyword]);

  // Prevent auto-scroll during input
  const preventScroll = useCallback(() => {
    const currentScrollPos = window.pageYOffset || document.documentElement.scrollTop;
    setTimeout(() => {
      window.scrollTo(0, currentScrollPos);
    }, 0);
  }, []);

  // Optimized input change handlers
  const handleCustomKeywordChange = useCallback((e) => {
    preventScroll();
    setCustomKeyword(e.target.value);
  }, [preventScroll]);

  const handleCustomRegionalKeywordChange = useCallback((e) => {
    preventScroll();
    setCustomRegionalKeyword(e.target.value);
  }, [preventScroll]);

  const handleKeyPressCustomKeyword = useCallback((e) => {
    if (e.key === 'Enter') {
      handleAddCustomKeyword('primary');
    }
  }, [handleAddCustomKeyword]);

  const handleKeyPressCustomRegionalKeyword = useCallback((e) => {
    if (e.key === 'Enter') {
      handleAddCustomKeyword('regional');
    }
  }, [handleAddCustomKeyword]);

  // Stabilize style objects to prevent re-rendering
  const textFieldStyle = useMemo(() => ({ flex: 1 }), []);
  const keywordBoxStyle = useMemo(() => ({ display: 'flex', gap: 1, alignItems: 'center' }), []);
  const keywordContainerStyle = useMemo(() => ({ mt: 3, mb: 2 }), []);

  // Card functionality for generated posts
  const togglePostSelection = (postId) => {
    setWizardData(prev => ({
      ...prev,
      selectedPosts: prev.selectedPosts.includes(postId)
        ? prev.selectedPosts.filter(id => id !== postId)
        : [...prev.selectedPosts, postId]
    }));
  };

  const generatePosts = async () => {
    setGenerating(true);
    setGenerationProgress({ current: 0, total: wizardData.postCount, step: 'Vorbereitung...' });
    
    try {
      const response = await fetch(`/api/businesses/${selectedBusiness.locationId}/posts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keywords: wizardData.targetKeywords,
          regionalKeywords: wizardData.targetRegionalKeywords,
          businessData: {
            id: selectedBusiness.id,
            locationId: selectedBusiness.locationId,
            businessName: selectedBusiness.businessName,
            description: selectedBusiness.description,
            categories: selectedBusiness.categories,
            address: selectedBusiness.address,
            phoneNumber: selectedBusiness.phoneNumber,
            websiteUrl: selectedBusiness.websiteUrl,
            lat: selectedBusiness.lat,
            lng: selectedBusiness.lng,
            customAiPrompt: selectedBusiness?.customAiPrompt || ''
          },
          count: wizardData.postCount,
          seoOptimized: true
        })
      });

      // Simuliere Fortschritt für Batches
      const batchSize = 5;
      const totalBatches = Math.ceil(wizardData.postCount / batchSize);
      
      for (let i = 1; i <= totalBatches; i++) {
        const currentPosts = Math.min(i * batchSize, wizardData.postCount);
        setGenerationProgress({ 
          current: currentPosts, 
          total: wizardData.postCount, 
          step: `Batch ${i}/${totalBatches} wird generiert...` 
        });
        
        // Kurze Verzögerung für visuellen Effekt
        if (i < totalBatches) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.posts) {
          setGenerationProgress({ current: data.posts.length, total: wizardData.postCount, step: 'Abgeschlossen!' });
          setWizardData(prev => ({
            ...prev,
            generatedPosts: data.posts,
            selectedPosts: data.posts.map(post => post.id) // Alle vorausgewählt
          }));
          
          // Generate images if enabled
          if (wizardData.generateImages) {
            setGenerationProgress({ current: data.posts.length, total: wizardData.postCount, step: 'Bilder werden generiert...' });
            await generateImagesForPosts();
          }
          
          // Kurz warten, damit Benutzer den Erfolg sehen kann
          setTimeout(() => {
            handleNext();
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error generating posts:', error);
      setGenerationProgress({ current: 0, total: wizardData.postCount, step: 'Fehler beim Generieren' });
    }
    
    setTimeout(() => {
      setGenerating(false);
      setGenerationProgress({ current: 0, total: 0, step: '' });
    }, 2000);
  };

  // Image Generation Functions
  const fetchCostEstimate = async (postCount, quality = 'medium') => {
    try {
      const response = await fetch(`/api/businesses/${selectedBusiness.locationId}/posts/image-cost-estimate?count=${postCount}&quality=${quality}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.estimation;
      }
    } catch (error) {
      console.error('Error fetching cost estimate:', error);
    }
    return { estimatedCost: 0 };
  };

  const updateCostEstimate = async () => {
    if (wizardData.generateImages) {
      const selectedCount = wizardData.selectedPosts.length;
      const estimate = await fetchCostEstimate(selectedCount, wizardData.imageQuality);
      setWizardData(prev => ({
        ...prev,
        imageGeneration: {
          ...prev.imageGeneration,
          estimatedCost: estimate.estimatedCost
        }
      }));
    }
  };

  const generateImagesForPosts = async () => {
    if (!wizardData.generateImages || wizardData.selectedPosts.length === 0) return;
    
    setWizardData(prev => ({
      ...prev,
      imageGeneration: {
        ...prev.imageGeneration,
        status: 'generating'
      }
    }));

    try {
      // Get selected posts objects instead of just IDs
      const selectedPostObjects = wizardData.generatedPosts.filter(post => 
        wizardData.selectedPosts.includes(post.id)
      );
      
      const response = await fetch(`/api/businesses/${selectedBusiness.locationId}/posts/generate-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          posts: selectedPostObjects,
          quality: wizardData.imageQuality,
          size: wizardData.imageSize
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update wizard data with image generation results
        const results = {};
        data.results.forEach(result => {
          results[result.postId] = {
            success: result.success,
            cost: result.cost,
            error: result.error
          };
        });

        setWizardData(prev => ({
          ...prev,
          imageGeneration: {
            ...prev.imageGeneration,
            status: 'completed',
            results: results,
            totalCost: data.summary.totalCost
          }
        }));
      } else {
        throw new Error('Failed to generate images');
      }
    } catch (error) {
      console.error('Error generating images:', error);
      setWizardData(prev => ({
        ...prev,
        imageGeneration: {
          ...prev.imageGeneration,
          status: 'error'
        }
      }));
    }
  };

  // Update cost estimate when selection changes
  useEffect(() => {
    updateCostEstimate();
  }, [wizardData.selectedPosts, wizardData.imageQuality, wizardData.generateImages]);

  const generateImagesForSelectedPosts = async () => {
    if (wizardData.selectedPosts.length === 0) return;
    
    setWizardData(prev => ({
      ...prev,
      imageGeneration: {
        ...prev.imageGeneration,
        status: 'generating'
      }
    }));

    try {
      // Get selected posts objects instead of just IDs
      const selectedPostObjects = wizardData.generatedPosts.filter(post => 
        wizardData.selectedPosts.includes(post.id)
      );
      
      const response = await fetch(`/api/businesses/${selectedBusiness.locationId}/posts/generate-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          posts: selectedPostObjects,
          quality: wizardData.imageQuality || 'medium',
          size: wizardData.imageSize || '1024x1024'
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update wizard data with image generation results
        const updatedPosts = wizardData.generatedPosts.map(post => {
          const result = data.results.find(r => r.postId === post.id);
          if (result && result.success) {
            return {
              ...post,
              generatedImageUrl: result.imageUrl,
              imageGenerationStatus: 'completed',
              imageGenerationCost: result.cost,
              imageGeneratedAt: new Date(),
              imagePrompt: result.imagePrompt,
              imageConfig: result.imageConfig
            };
          }
          return post;
        });

        setWizardData(prev => ({
          ...prev,
          generatedPosts: updatedPosts,
          imageGeneration: {
            ...prev.imageGeneration,
            status: 'completed',
            totalCost: data.summary.totalCost
          }
        }));
        
        console.log(`✅ Images generated for ${data.summary.successful} posts`);
      } else {
        throw new Error('Failed to generate images');
      }
    } catch (error) {
      console.error('Error generating images:', error);
      setWizardData(prev => ({
        ...prev,
        imageGeneration: {
          ...prev.imageGeneration,
          status: 'error'
        }
      }));
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    // Use edited version if available, otherwise original content
    const currentContent = (wizardData.editedPosts && wizardData.editedPosts[post.id]) || post.content;
    setEditedContent(currentContent);
    setEditingMediaUrl(post.mediaUrl || '');
    setEditingMediaFile(null);
  };

  const handleMediaFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Create preview URL from file
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditingMediaUrl(e.target.result); // Set data URL as preview
        setEditingMediaFile(file); // Store file for later upload
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveEdit = async () => {
    if (editingPost && editedContent.trim()) {
      let finalMediaUrl = editingMediaUrl;

      // If file was selected, upload it to ImageKit first
      if (editingMediaFile && editingMediaFile.size > 0) {
        try {
          const formData = new FormData();
          formData.append('file', editingMediaFile);

          const uploadResponse = await fetch('/api/imagekit/upload', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            body: formData
          });

          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.statusText}`);
          }

          const result = await uploadResponse.json();
          finalMediaUrl = result.url;
          console.log('Media uploaded to ImageKit:', finalMediaUrl);
        } catch (error) {
          console.error('Error uploading media:', error);
          alert('Fehler beim Hochladen der Datei. Bitte versuchen Sie es später erneut.');
          return;
        }
      }

      // Update post with edited content and media URL
      setWizardData(prev => {
        const updatedGeneratedPosts = prev.generatedPosts.map(p =>
          p.id === editingPost.id
            ? { ...p, mediaUrl: finalMediaUrl }
            : p
        );
        return {
          ...prev,
          generatedPosts: updatedGeneratedPosts,
          editedPosts: {
            ...(prev.editedPosts || {}),
            [editingPost.id]: editedContent.trim()
          }
        };
      });
      setEditingPost(null);
      setEditedContent('');
      setEditingMediaUrl('');
      setEditingMediaFile(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
    setEditedContent('');
    setEditingMediaUrl('');
    setEditingMediaFile(null);
  };

  const getPostContent = (post) => {
    if (!post || !post.content) return '';
    return (wizardData.editedPosts && wizardData.editedPosts[post.id]) || post.content;
  };


  const calculateKeywordCoverage = (posts) => {
    const usedKeywords = new Set();
    posts.forEach(post => {
      if (post.targetKeywords) {
        post.targetKeywords.forEach(keyword => usedKeywords.add(keyword));
      }
    });

    const totalKeywords = wizardData.targetKeywords.length + wizardData.targetRegionalKeywords.length;
    return totalKeywords > 0 ? Math.round((usedKeywords.size / totalKeywords) * 100) : 0;
  };

  const finalizeWizard = async () => {
    // Prevent multiple submissions
    if (isSubmitting) return;

    try {
      // Set submitting state and disable button
      setIsSubmitting(true);

      // Save scheduled posts
      await onComplete({
        strategy: wizardData.strategy,
        scheduledPosts: wizardData.scheduledPosts,
        publishingPlan: wizardData.publishingPlan,
        keywords: wizardData.targetKeywords,
        regionalKeywords: wizardData.targetRegionalKeywords
      });
      
      // Close wizard after successful submission
      onClose();
    } catch (error) {
      console.error('Error finalizing wizard:', error);
      
      // Show error to user
      alert('Fehler beim Erstellen der Kampagne. Bitte versuchen Sie es erneut.');
    } finally {
      // Always reset submitting state
      setIsSubmitting(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderStrategyStep();
      case 1:
        return <GenerationStep />;
      case 2:
        return <SelectionStep />;
      case 3:
        return <SchedulingStep />;
      case 4:
        return <PreviewStep />;
      default:
        return null;
    }
  };

  // Step Components
  // Hilfsfunktion für Ranking-Indikatoren
  const getKeywordRankingInfo = (keyword) => {
    if (!rankingData || !rankingData.trackedKeywords) return null;
    
    const trackedKeyword = rankingData.trackedKeywords.find(k => k.keyword === keyword);
    if (!trackedKeyword) return null;
    
    const rank = trackedKeyword.averageRank;
    let color = 'default';
    let bgColor = 'rgba(0,0,0,0.1)';
    
    if (rank <= 3) {
      color = 'success';
      bgColor = 'rgba(76,175,80,0.2)';
    } else if (rank <= 10) {
      color = 'warning';
      bgColor = 'rgba(255,193,7,0.2)';
    } else {
      color = 'error';
      bgColor = 'rgba(244,67,54,0.2)';
    }
    
    return {
      rank,
      color,
      bgColor,
      isTracked: true
    };
  };

  // Memoized handlers for the StrategyStep component
  const handleStrategyChangeMemo = useCallback((key) => {
    handleStrategyChange(key);
  }, [handleStrategyChange]);

  const handleCustomKeywordChangeMemo = useCallback((e) => {
    handleCustomKeywordChange(e);
  }, [handleCustomKeywordChange]);

  const handleKeyPressCustomKeywordMemo = useCallback((e) => {
    handleKeyPressCustomKeyword(e);
  }, [handleKeyPressCustomKeyword]);

  const handleAddCustomKeywordMemo = useCallback((type, keyword) => {
    handleAddCustomKeyword(type, keyword);
  }, [handleAddCustomKeyword]);

  const removeKeywordMemo = useCallback((keyword, type) => {
    removeKeyword(keyword, type);
  }, [removeKeyword]);

  const handleCustomRegionalKeywordChangeMemo = useCallback((e) => {
    handleCustomRegionalKeywordChange(e);
  }, [handleCustomRegionalKeywordChange]);

  const handleKeyPressCustomRegionalKeywordMemo = useCallback((e) => {
    handleKeyPressCustomRegionalKeyword(e);
  }, [handleKeyPressCustomRegionalKeyword]);

  const renderStrategyStep = () => (
    <StrategyStep
      strategies={strategies}
      wizardData={wizardData}
      handleStrategyChange={handleStrategyChangeMemo}
      handleCustomPostSettingsChange={handleCustomPostSettingsChange}
      availableKeywords={availableKeywords}
      customKeyword={customKeyword}
      handleCustomKeywordChange={handleCustomKeywordChangeMemo}
      handleKeyPressCustomKeyword={handleKeyPressCustomKeywordMemo}
      handleAddCustomKeyword={handleAddCustomKeywordMemo}
      removeKeyword={removeKeywordMemo}
      getKeywordRankingInfo={getKeywordRankingInfo}
      textFieldStyle={textFieldStyle}
      availableRegionalKeywords={availableRegionalKeywords}
      customRegionalKeyword={customRegionalKeyword}
      handleCustomRegionalKeywordChange={handleCustomRegionalKeywordChangeMemo}
      handleKeyPressCustomRegionalKeyword={handleKeyPressCustomRegionalKeywordMemo}
    />
  );

  const GenerationStep = () => (
    <Box sx={{ py: 2, textAlign: 'center' }}>
      {!generating ? (
        <Box>
          <AIIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            KI-Posts generieren
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            Basierend auf Ihrer {strategies[wizardData.strategy].name} werden jetzt {wizardData.postCount} 
            SEO-optimierte Posts für {selectedBusiness.businessName} generiert.
          </Typography>
          
          <Paper sx={{ p: 3, mb: 4, maxWidth: 500, mx: 'auto' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="h6" color="primary.main">
                  {wizardData.targetKeywords.length}
                </Typography>
                <Typography variant="caption">Haupt-Keywords</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" color="success.main">
                  {wizardData.targetRegionalKeywords.length}
                </Typography>
                <Typography variant="caption">Regional-Keywords</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Image Generation Options */}
          <Paper sx={{ p: 3, mb: 4, maxWidth: 600, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ImageIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                KI-Bilder generieren
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Checkbox
                checked={wizardData.generateImages}
                onChange={(e) => setWizardData(prev => ({
                  ...prev,
                  generateImages: e.target.checked,
                  imageGeneration: {
                    ...prev.imageGeneration,
                    enabled: e.target.checked
                  }
                }))}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2">
                Automatisch passende Bilder für GMB-Posts generieren
              </Typography>
            </Box>

            {wizardData.generateImages && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Bildqualität</InputLabel>
                      <Select
                        value={wizardData.imageQuality}
                        onChange={(e) => setWizardData(prev => ({
                          ...prev,
                          imageQuality: e.target.value
                        }))}
                      >
                        <MenuItem value="low">Niedrig (~$0.01/Bild)</MenuItem>
                        <MenuItem value="medium">Mittel (~$0.03/Bild)</MenuItem>
                        <MenuItem value="high">Hoch (~$0.12/Bild)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                      <CostIcon sx={{ mr: 1, color: 'success.main' }} />
                      <Typography variant="body2" color="success.main">
                        Geschätzte Kosten: ${(wizardData.imageGeneration.estimatedCost || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Optimiert für Google My Business (1024x1024px, JPEG)
                </Typography>
              </Box>
            )}
          </Paper>

          <Button
            variant="contained"
            size="large"
            startIcon={<AIIcon />}
            onClick={generatePosts}
            sx={{ 
              bgcolor: '#4CAF50',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': { bgcolor: '#45A049' }
            }}
          >
            {wizardData.postCount} Posts generieren
          </Button>
        </Box>
      ) : (
        <Box>
          <CircularProgress size={80} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Posts werden generiert...
          </Typography>
          
          {generationProgress.total > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                {generationProgress.step}
              </Typography>
              <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                {generationProgress.current} / {generationProgress.total} Posts
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(generationProgress.current / generationProgress.total) * 100}
                sx={{ maxWidth: 400, mx: 'auto', height: 8, borderRadius: 4 }} 
              />
            </Box>
          )}
          
          {generationProgress.total === 0 && (
            <>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                KI erstellt authentische Inhalte basierend auf Website und Bewertungen
              </Typography>
              <LinearProgress sx={{ maxWidth: 400, mx: 'auto' }} />
            </>
          )}
        </Box>
      )}
    </Box>
  );

  const SelectionStep = () => (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Generierte Posts auswählen
        </Typography>
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setWizardData(prev => ({ ...prev, selectedPosts: prev.generatedPosts.map(p => p.id) }))}
            sx={{ mr: 1 }}
          >
            Alle auswählen
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setWizardData(prev => ({ ...prev, selectedPosts: [] }))}
          >
            Alle abwählen
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<ImageIcon />}
            onClick={() => generateImagesForSelectedPosts()}
            disabled={wizardData.selectedPosts.length === 0 || wizardData.imageGeneration?.status === 'generating'}
            sx={{ ml: 1 }}
          >
            {wizardData.imageGeneration?.status === 'generating' ? 'Generiere...' : 'Bilder generieren'}
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {wizardData.selectedPosts.length} von {wizardData.generatedPosts.length} Posts ausgewählt
      </Typography>

      {/* Image Generation Status */}
      {wizardData.imageGeneration?.status === 'generating' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 2, bgcolor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="primary">
            Bilder werden generiert...
          </Typography>
        </Box>
      )}

      {wizardData.imageGeneration?.status === 'completed' && wizardData.imageGeneration?.totalCost > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
          <CheckIcon sx={{ fontSize: 20, color: 'success.main' }} />
          <Typography variant="body2" color="success.main">
            Bilder generiert! Kosten: ${wizardData.imageGeneration?.totalCost?.toFixed(3) || '0.000'}
          </Typography>
        </Box>
      )}

      {wizardData.imageGeneration?.status === 'error' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 2, bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 1 }}>
          <Typography variant="body2" color="error">
            Bildgenerierung fehlgeschlagen
          </Typography>
        </Box>
      )}

      {/* Enterprise Card Grid für generierte Posts */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {wizardData.generatedPosts.map((post, index) => (
          <Grid item xs={12} md={6} lg={4} key={post.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: wizardData.selectedPosts.includes(post.id) ? '2px solid' : '1px solid',
                borderColor: wizardData.selectedPosts.includes(post.id) ? 'primary.main' : 'divider',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: wizardData.selectedPosts.includes(post.id) ? 'primary.50' : 'background.paper',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                },
                position: 'relative'
              }}
              onClick={(e) => {
                // Only toggle if not clicking on interactive elements
                if (e.target.closest('button, [role="checkbox"], input')) return;
                togglePostSelection(post.id);
              }}
            >
              {/* Selection Indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  zIndex: 1
                }}
              >
                <Checkbox
                  checked={wizardData.selectedPosts.includes(post.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    togglePostSelection(post.id);
                  }}
                  size="small"
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: '50%',
                    boxShadow: 1
                  }}
                />
              </Box>

              <CardContent sx={{ flexGrow: 1, pt: 2, pb: 2 }}>
                {/* Post Content */}
                <Box sx={{ mb: 3 }}>
                  {editingPost === post.id ? (
                    <TextField
                      multiline
                      fullWidth
                      rows={6}
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      variant="outlined"
                      size="small"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          handleSaveEdit(post);
                        }
                        if (e.key === 'Escape') {
                          setEditingPost(null);
                          setEditedContent('');
                        }
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                          lineHeight: 1.6
                        }
                      }}
                    />
                  ) : (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        display: '-webkit-box',
                        WebkitLineClamp: 6,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {getPostContent(post)}
                    </Typography>
                  )}
                  
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {getPostContent(post).length} Zeichen
                  </Typography>
                </Box>

                {/* Keywords */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Keywords
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {post.targetKeywords?.slice(0, 3).map((keyword, keywordIndex) => (
                      <Chip 
                        key={keywordIndex} 
                        label={keyword} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    ))}
                    {post.targetKeywords?.length > 3 && (
                      <Chip 
                        label={`+${post.targetKeywords.length - 3}`} 
                        size="small" 
                        color="primary"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>
                </Box>

              </CardContent>

              {/* Actions */}
              <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                {editingPost === post.id ? (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveEdit(post);
                      }}
                      sx={{ flex: 1 }}
                    >
                      Speichern
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloseIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingPost(null);
                        setEditedContent('');
                      }}
                    >
                      Abbrechen
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPost(post);
                    }}
                    fullWidth
                  >
                    Bearbeiten
                  </Button>
                )}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const SchedulingStep = () => (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Veröffentlichungsplan erstellen
      </Typography>

    

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Zeitplan-Einstellungen
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Veröffentlichungsmodus</InputLabel>
              <Select
                value={wizardData.schedulingMode}
                onChange={(e) => setWizardData(prev => ({ ...prev, schedulingMode: e.target.value }))}
                label="Veröffentlichungsmodus"
              >
                <MenuItem value="interval">Feste Intervalle</MenuItem>
                <MenuItem value="workdays">Nur Werktage</MenuItem>
                <MenuItem value="custom">Benutzerdefiniert</MenuItem>
              </Select>
            </FormControl>

            {wizardData.schedulingMode === 'interval' && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Intervall (Tage)</InputLabel>
                <Select
                  value={wizardData.intervalDays}
                  onChange={(e) => setWizardData(prev => ({ ...prev, intervalDays: e.target.value }))}
                  label="Intervall (Tage)"
                >
                  <MenuItem value={1}>Täglich</MenuItem>
                  <MenuItem value={2}>Alle 2 Tage</MenuItem>
                  <MenuItem value={3}>Alle 3 Tage</MenuItem>
                  <MenuItem value={7}>Wöchentlich</MenuItem>
                  <MenuItem value={14}>Alle 2 Wochen</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              label="Startdatum"
              type="date"
              value={format(wizardData.startDate, 'yyyy-MM-dd')}
              onChange={(e) => setWizardData(prev => ({ ...prev, startDate: new Date(e.target.value) }))}
              fullWidth
              sx={{ mb: 3 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Uhrzeit"
              type="time"
              value={wizardData.startTime}
              onChange={(e) => setWizardData(prev => ({ ...prev, startTime: e.target.value }))}
              fullWidth
              sx={{ mb: 3 }}
              InputLabelProps={{ shrink: true }}
              helperText="Tägliche Veröffentlichungszeit"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Checkbox
                checked={wizardData.excludeWeekends}
                onChange={(e) => setWizardData(prev => ({ ...prev, excludeWeekends: e.target.checked }))}
              />
              <Typography variant="body2">
                Wochenenden ausschließen
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={calculateScheduleAndNext}
              startIcon={<CalendarIcon />}
              fullWidth
              size="large"
            >
              Zeitplan berechnen
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Ausgewählte Posts
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={`${wizardData.selectedPosts.length} Posts ausgewählt`}
                color="primary"
                icon={<PostIcon />}
              />
            </Box>

            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {wizardData.generatedPosts
                .filter(post => wizardData.selectedPosts.includes(post.id))
                .map((post, index) => (
                <ListItem key={post.id} divider>
                  <ListItemText
                    primary={`Post ${index + 1}`}
                    secondary={getPostContent(post).substring(0, 80) + '...'}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const PreviewStep = () => (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Kampagnen-Vorschau
      </Typography>

      {wizardData.publishingPlan && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {wizardData.publishingPlan.totalPosts}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Posts geplant
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e8' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                {wizardData.publishingPlan.duration}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Tage Laufzeit
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {wizardData.publishingPlan.keywordCoverage}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Keyword-Abdeckung
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Zeitplan-Übersicht
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary">
                  Start: {format(wizardData.publishingPlan.startDate, 'dd.MM.yyyy', { locale: de })} • 
                  Ende: {format(wizardData.publishingPlan.endDate, 'dd.MM.yyyy', { locale: de })} • 
                  Frequenz: {wizardData.publishingPlan.frequency}
                </Typography>
              </Box>

              <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                {wizardData.scheduledPosts.map((post, index) => (
                  <ListItem key={post.id} divider sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Tag {post.dayNumber}:
                          </Typography>
                          <Typography variant="body2">
                            {format(post.scheduledFor, 'dd.MM.yyyy (EEEE)', { locale: de })}
                          </Typography>
                          <Chip label={`SEO: ${post.localSeoScore || 85}%`} size="small" variant="outlined" />
                          {post.mediaUrl && (
                            <Chip
                              icon={<PhotoIcon />}
                              label="Mit Medien"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={getPostContent(post).substring(0, 80) + '...'}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="body"
      disableScrollLock={true}
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AIIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Content-Kampagne erstellen
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedBusiness?.businessName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                icon={
                  <Avatar
                    sx={{
                      bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                      color: 'white',
                      width: 40,
                      height: 40
                    }}
                  >
                    {index < activeStep ? <CheckIcon /> : step.icon}
                  </Avatar>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {step.label}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {step.description}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<BackIcon />}
        >
          Zurück
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={finalizeWizard}
              size="large"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <PublishIcon />}
              sx={{ 
                bgcolor: '#4CAF50',
                px: 4,
                '&:hover': { bgcolor: '#45A049' },
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? 'Wird gestartet...' : 'Kampagne starten'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 1 && !generating && wizardData.generatedPosts.length === 0) ||
                (activeStep === 2 && wizardData.selectedPosts.length === 0) ||
                generating
              }
              endIcon={<NextIcon />}
            >
              {activeStep === 1 && wizardData.generatedPosts.length === 0 
                ? 'Posts generieren' 
                : 'Weiter'
              }
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>

    {/* Edit Post Dialog */}
    <Dialog
      open={editingPost !== null}
      onClose={handleCancelEdit}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Post bearbeiten
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Post Content */}
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Passen Sie den Post-Inhalt nach Ihren Wünschen an:
          </Typography>
          <TextField
            multiline
            rows={8}
            fullWidth
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            placeholder="Post-Inhalt bearbeiten..."
            variant="outlined"
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Länge: {editedContent.length} Zeichen (empfohlen: 800-1200)
          </Typography>

          {/* Media Upload Section */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
            Medien hinzufügen (optional):
          </Typography>

          {/* File Upload */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
              Option 1: Datei hochladen
            </Typography>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: editingMediaFile ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
              }}
            >
              <input
                type="file"
                id="media-file-input"
                hidden
                onChange={handleMediaFileSelect}
                accept="image/*,video/*"
              />
              <label htmlFor="media-file-input" style={{ cursor: 'pointer', display: 'block' }}>
                <PhotoIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {editingMediaFile ? editingMediaFile.name : 'Klicken zum Hochladen'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  oder Datei hierher ziehen
                </Typography>
              </label>
            </Box>
          </Box>

          {/* URL Input */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
              Option 2: URL eingeben
            </Typography>
            <TextField
              fullWidth
              label="Bild/Video URL"
              type="url"
              placeholder="https://example.com/image.jpg"
              value={editingMediaFile ? '' : editingMediaUrl}
              onChange={(e) => setEditingMediaUrl(e.target.value)}
              disabled={!!editingMediaFile}
              variant="outlined"
              helperText={editingMediaFile ? 'Klicken Sie auf "Datei löschen" unten, um eine URL statt einer Datei zu verwenden' : 'Geben Sie die URL zu einem Bild oder Video ein'}
              size="small"
            />
          </Box>

          {/* Preview */}
          {editingMediaUrl && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>Vorschau:</Typography>
                {editingMediaFile && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setEditingMediaFile(null);
                      setEditingMediaUrl('');
                    }}
                  >
                    Datei löschen
                  </Button>
                )}
              </Box>
              {editingMediaUrl.toLowerCase().endsWith('.mp4') || editingMediaUrl.toLowerCase().includes('video') ? (
                <video width="100%" height="200" style={{ marginTop: 8, borderRadius: 4 }} controls>
                  <source src={editingMediaUrl} />
                </video>
              ) : (
                <img src={editingMediaUrl} alt="Preview" style={{ marginTop: 8, maxWidth: '100%', maxHeight: 200, borderRadius: 4 }} />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelEdit}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSaveEdit}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!editedContent.trim() || (editingMediaFile && editingMediaFile.size === 0)}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default PostsWizard;